import { GAME, schedule, score, type Click, type Spot } from "@/lib/game";
import { CONTACT_EMAIL } from "@/lib/site";
import type { MascotApi } from "./registry";

/**
 * A: the yellow stroke of the YA logo, alive. Plain DOM + SVG, one rAF loop.
 *
 * A reads the page, it doesn't own it. Any element with data-scene is a
 * scene; its data attributes say where A stands (data-perch, a selector),
 * what it wears (data-head / data-chest / data-hand), how it stands
 * (data-pose, data-expr) and what it says the first time (data-line).
 * Server-rendered pages therefore carry all of A's lines as plain markup.
 *
 * Modes: slot (glued inside a logo), perch (standing on an element), float
 * (bottom-right corner when no perch is visible), jump, stage (performing
 * in the playground), drag (held by the pointer), cloth (page transition).
 */

type Shape = { w: number; h: number; k: number; r: number; leg: number };
type Mode = "slot" | "perch" | "float" | "jump" | "stage" | "drag" | "cloth" | "game";
type Pt = { x: number; y: number };
type Act = { label: string; pri?: boolean; run: () => void };
type Want = { kind: "perch" | "float"; id: string; fn: () => Pt; scale: number };
type Jump = {
  fn: () => Pt; fromX: number; fromY: number; fromS: number; toS: number; t: number;
  crouch: number; dur: number; arc: number; spin: number; next: Mode; slotEl: Element | null; onLand?: () => void;
};
type JumpOpts = { keepBubble?: boolean; next: Mode; shape?: Shape; scale?: number; crouch?: number; dur?: number; arc?: number; spin?: number; slotEl?: Element | null; onLand?: () => void };

export type ClothLabel = { kick: string; title: string };
export type MascotOptions = {
  push: (href: string) => void;
  /** pathname → words printed on the cloth when travelling there */
  labels: Record<string, ClothLabel>;
};
export type MascotEngine = MascotApi & { start(): void; destroy(): void; routeChanged(pathname: string): void };

const LOGO: Shape = { w: 14.4, h: 70.7, k: 0.487, r: 1, leg: 0 };
const REST: Shape = { w: 30, h: 58, k: 0.3, r: 5, leg: 9 };
const INK = "#111212";

const EYE_PATH: Record<string, string> = {
  happy: "M-3.2,1.4 Q0,-3.4 3.2,1.4",
  sleep: "M-3.2,-0.4 Q0,2.4 3.2,-0.4",
  proud: "M-3.2,0.6 Q0,-2.6 3.2,0.6",
  dizzy: "M-2.8,-2.8 L2.8,2.8 M2.8,-2.8 L-2.8,2.8",
};
const MOUTH: Record<string, string> = {
  smile: `<path d="M-3.2,0 Q0,3 3.2,0" fill="none" stroke="${INK}" stroke-width="1.9" stroke-linecap="round"/>`,
  grin: `<path d="M-4.2,-0.6 Q0,5.6 4.2,-0.6 Z" fill="${INK}" stroke="${INK}" stroke-width="1" stroke-linejoin="round"/>`,
  o: `<ellipse rx="2.1" ry="2.7" fill="${INK}"/>`,
  flat: `<path d="M-2.6,0 H2.6" stroke="${INK}" stroke-width="1.9" stroke-linecap="round"/>`,
  yawn: `<ellipse rx="2.8" ry="3.6" fill="${INK}"/>`,
  wobble: `<path d="M-4,0 q1,-1.6 2,0 t2,0 t2,0 t2,0" fill="none" stroke="${INK}" stroke-width="1.7" stroke-linecap="round"/>`,
};
const EXPR_MOUTH: Record<string, string> = {
  normal: "smile", happy: "grin", surprised: "o", heart: "grin", sleep: "flat", sleepy: "yawn",
  dizzy: "wobble", proud: "smile", think: "flat", effort: "o",
};
const HEAD: Record<string, string> = {
  hardhat: `<path d="M-16,0 C-16,-16 16,-16 16,0 Z" fill="#fff" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/><path d="M0,-12.5 V-1" stroke="${INK}" stroke-width="1.6"/><path d="M-21,0 H21" stroke="${INK}" stroke-width="3.2" stroke-linecap="round"/>`,
  chef: `<path d="M-11,0 V-8 C-19,-9 -18,-22 -9,-19.5 C-8,-28 8,-28 9,-19.5 C18,-22 19,-9 11,-8 V0 Z" fill="#fff" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/><path d="M-11,-4 H11" stroke="${INK}" stroke-width="1.4"/>`,
  taxi: `<rect x="-2.5" y="-3" width="5" height="3.5" fill="${INK}"/><rect x="-15" y="-13" width="30" height="10.5" rx="2.6" fill="${INK}"/><text x="0" y="-5.2" text-anchor="middle" font-size="7.4" font-weight="800" letter-spacing="0.6" fill="#ffd600" style="font-family: var(--f-d)">TAXI</text>`,
};
const CHEST: Record<string, string> = {
  bowtie: `<path d="M0,0 L-7.5,-4.5 L-7.5,4.5 Z M0,0 L7.5,-4.5 L7.5,4.5 Z" fill="${INK}" stroke="${INK}" stroke-width="1.2" stroke-linejoin="round"/><circle r="2" fill="${INK}"/>`,
  envelope: `<rect x="-12" y="-8" width="24" height="16" fill="#fff" stroke="${INK}" stroke-width="1.9" stroke-linejoin="round"/><path d="M-12,-8 L0,1.5 L12,-8" fill="none" stroke="${INK}" stroke-width="1.7" stroke-linejoin="round"/>`,
};
const HAND: Record<string, string> = {
  wrench: `<path d="M0,2 L0,-12" stroke="#5b5f5c" stroke-width="3.4" stroke-linecap="round"/><path d="M-4.6,-13 A5,5 0 1 1 4.6,-13 L1.8,-12.2 L0,-15.2 L-1.8,-12.2 Z" fill="#5b5f5c"/>`,
  pen: `<path d="M-2.2,-4 L2.2,-4 L2.2,-16 L-2.2,-16 Z" fill="${INK}"/><path d="M-2.2,-4 L0,2.4 L2.2,-4 Z" fill="#fff" stroke="${INK}" stroke-width="1.2" stroke-linejoin="round"/>`,
};

/** Arm angles in degrees, 90 = straight down. */
function poseAngles(p: string, t: number): [number, number] {
  switch (p) {
    case "wave": return [112, -42 + 26 * Math.sin(t * 9)];
    case "point": return [112, 18];
    case "hips": return [138, 42];
    case "statue": return [-146, -34];
    case "tool": return [112, -28];
    case "hold": return [58, 122];
    case "air": return [-150, -30];
    case "drag": return [-118, -62];
    case "think": return [112, -112];
    case "tada": return [-160, -20];
    case "pull": return [174, 160];
    case "pullL": return [20, 6];
    default: return [112, 68];
  }
}

const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeInOut2 = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const smooth = (t: number) => t * t * (3 - 2 * t);
const rad = (d: number) => (d * Math.PI) / 180;
const normPath = (p: string) => p.replace(/\/+$/, "") || "/";

export function createMascot(opts: MascotOptions): MascotEngine {
  const RM = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const $ = <T extends Element = HTMLElement>(s: string, r: ParentNode = document) => r.querySelector<T>(s);
  const $$ = <T extends Element = HTMLElement>(s: string, r: ParentNode = document) => [...r.querySelectorAll<T>(s)];
  const root = document.documentElement;

  /* ---------- lifetime: every timer and listener dies with the engine ---------- */
  let alive = true;
  let started = false;
  let raf = 0;
  const timers = new Set<number>();
  const later = (ms: number, fn: () => void) => {
    const id = window.setTimeout(() => {
      timers.delete(id);
      if (alive) fn();
    }, ms);
    timers.add(id);
    return id;
  };
  const wait = (ms: number) => new Promise<void>((res) => later(RM ? Math.min(ms, 300) : ms, res));
  const frames = (n: number) =>
    new Promise<void>((res) => {
      const step = () => (n-- <= 0 ? res() : requestAnimationFrame(step));
      requestAnimationFrame(step);
    });
  const unlisten: (() => void)[] = [];
  const on = (t: EventTarget, type: string, fn: (e: never) => void, o?: AddEventListenerOptions | boolean) => {
    t.addEventListener(type, fn as EventListener, o);
    unlisten.push(() => t.removeEventListener(type, fn as EventListener, o));
  };

  /* ---------- the overlay (rendered once by <Mascot />) ---------- */
  const layer = $<SVGSVGElement>("#aLayer")!;
  const A = $<SVGGElement>("#A")!;
  const body = $<SVGPathElement>("#aBody")!;
  const eyeL = $<SVGGElement>("#eyeL")!, eyeR = $<SVGGElement>("#eyeR")!;
  const eoL = $<SVGEllipseElement>(".eo", eyeL)!, eoR = $<SVGEllipseElement>(".eo", eyeR)!;
  const epL = $<SVGPathElement>(".ep", eyeL)!, epR = $<SVGPathElement>(".ep", eyeR)!;
  const mouth = $<SVGGElement>("#aMouth")!;
  const armLEl = $<SVGLineElement>("#armL")!, armREl = $<SVGLineElement>("#armR")!;
  const handLEl = $<SVGCircleElement>("#handL")!, handREl = $<SVGCircleElement>("#handR")!;
  const legs = $<SVGGElement>("#aLegs")!;
  const legL = $<SVGLineElement>("#legL")!, legR = $<SVGLineElement>("#legR")!;
  const footL = $<SVGLineElement>("#footL")!, footR = $<SVGLineElement>("#footR")!;
  const blushL = $<SVGEllipseElement>("#blushL")!, blushR = $<SVGEllipseElement>("#blushR")!;
  const headG = $<SVGGElement>("#aHead")!, chestG = $<SVGGElement>("#aChest")!, handG = $<SVGGElement>("#aHand")!;
  const hit = $("#aHit")!;
  const bubble = $("#bubble")!, bText = $("#bText")!, bActs = $("#bActs")!;
  const zzz = $("#zzz")!;
  const cloth = $("#cloth")!, clothKick = $("#clothKick")!, clothTitle = $("#clothTitle")!;

  /* ---------- the page (changes on every route) ---------- */
  let navLogo: SVGSVGElement | null = null;
  let bigLogo: SVGSVGElement | null = null;
  let stageLogo: SVGSVGElement | null = null;
  let stagePerchEl: HTMLElement | null = null;
  let sceneEls: HTMLElement[] = [];
  let path = normPath(location.pathname);
  function refreshPage() {
    navLogo = $<SVGSVGElement>("#navLogo");
    bigLogo = $<SVGSVGElement>("#bigLogo");
    stageLogo = $<SVGSVGElement>("#stageLogo");
    stagePerchEl = $("#stagePerch");
    sceneEls = $$("main [data-scene], footer[data-scene]");
    path = normPath(location.pathname);
  }
  const setState = (s: "big" | "navintro" | "out" | "nav") => (root.dataset.a = s);

  const small = () => innerWidth < 700;
  const restScale = () => (small() ? 1.05 : 1.42);

  const M = {
    mode: "slot" as Mode, slotEl: null as Element | null,
    vx: -200, vy: -200, s: 3, sT: 3,
    shp: { ...LOGO }, shpT: { ...LOGO },
    sq: 1, sqV: 0, sqT: 1,
    rot: 0, rotT: 0, spin: 0,
    eyeS: 0, eyeST: 0, limbS: 0, limbST: 0, alpha: 1, alphaT: 1,
    look: { x: 0, y: 0 }, lookT: { x: 0, y: 0 }, lookOverride: null as Pt | null,
    blink: 1, expr: "normal", pose: "rest",
    armL: 112, armR: 68, legSplay: 0,
    blush: 0, blushT: 0,
    head: null as string | null, chest: null as string | null, hand: null as string | null, propS: 0, propST: 0,
    sceneId: null as string | null, kind: null as string | null, t: 0,
    scenePose: "rest", sceneExpr: "normal",
    walkAmp: 0, walkPh: 0,
    pin: null as { scene: string; el: Element } | null,
  };

  /** Where the A stroke sits inside a logo svg, in screen pixels. */
  function slotFor(svg: Element) {
    const r = svg.getBoundingClientRect();
    const sc = Math.min(r.width / 118, r.height / 78);
    const ox = r.left + (r.width - 118 * sc) / 2 - 1 * sc;
    const oy = r.top + (r.height - 78 * sc) / 2 - 17 * sc;
    return { x: ox + 108.6 * sc, y: oy + 91.6 * sc, s: sc };
  }
  function perchPos(el: Element): Pt {
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width * (parseFloat((el as HTMLElement).dataset.px ?? "") || 0.5), y: r.top };
  }
  const floatPos = (): Pt => ({ x: innerWidth - (small() ? 46 : 92), y: innerHeight - (small() ? 16 : 26) });

  /* ---------- face ---------- */
  let exprShown = "";
  function applyExpr() {
    if (M.expr === exprShown) return;
    exprShown = M.expr;
    const special = EYE_PATH[M.expr] || (M.expr === "heart" || M.expr === "effort" ? M.expr : null);
    ([[eoL, epL, 1], [eoR, epR, -1]] as const).forEach(([eo, ep, side]) => {
      if (special === "effort") {
        eo.style.display = "none";
        ep.setAttribute("d", side > 0 ? "M-3,-2.8 L2.4,0 L-3,2.8" : "M3,-2.8 L-2.4,0 L3,2.8");
        ep.setAttribute("fill", "none");
        ep.setAttribute("stroke", INK);
        ep.style.display = "";
      } else if (special === "heart") {
        eo.style.display = "none";
        ep.setAttribute("d", "M0,3.4 C-6.4,-0.8 -3.6,-6.4 0,-3.1 C3.6,-6.4 6.4,-0.8 0,3.4 Z");
        ep.setAttribute("fill", "#e5484d");
        ep.setAttribute("stroke", "none");
        ep.style.display = "";
      } else if (special) {
        eo.style.display = "none";
        ep.setAttribute("d", special);
        ep.setAttribute("fill", "none");
        ep.setAttribute("stroke", INK);
        ep.style.display = "";
      } else {
        eo.style.display = "";
        ep.style.display = "none";
      }
    });
    mouth.innerHTML = MOUTH[EXPR_MOUTH[M.expr] || "smile"];
    M.blushT = M.expr === "effort" ? 0.95 : M.expr === "happy" || M.expr === "heart" ? 0.75 : 0;
  }

  function setCostume({ head = null, chest = null, hand = null }: { head?: string | null; chest?: string | null; hand?: string | null } = {}) {
    if (head === M.head && chest === M.chest && hand === M.hand) return;
    M.head = head; M.chest = chest; M.hand = hand;
    headG.innerHTML = head ? HEAD[head] ?? "" : "";
    chestG.innerHTML = chest ? CHEST[chest] ?? "" : "";
    handG.innerHTML = hand ? HAND[hand] ?? "" : "";
    M.propS = 0;
    M.propST = head || chest || hand ? 1 : 0;
  }

  /* ---------- render ---------- */
  let head: Pt = { x: 0, y: 0 };
  let box = { l: 0, t: 0, w: 0, h: 0 };
  const f = (n: number, d = 2) => n.toFixed(d);
  function render() {
    const { w, h, k, r, leg } = M.shp;
    const S = M.s;
    const sy = M.sq;
    const sx = M.sq < 1 ? 1 + (1 - M.sq) * 0.9 : 1 - (M.sq - 1) * 0.55;
    const yb = -leg - r;
    const pc = (yb - h / 2) * S * sy;
    A.setAttribute("transform", `translate(${f(M.vx, 1)} ${f(M.vy, 1)}) translate(0 ${f(pc, 1)}) rotate(${f(M.rot + M.spin)}) translate(0 ${f(-pc, 1)}) scale(${f(S * sx, 4)} ${f(S * sy, 4)})`);
    A.style.opacity = f(M.alpha, 3);

    body.setAttribute("d", `M${f(-w / 2)},${f(yb)} L${f(w / 2)},${f(yb)} L${f(w / 2 - k * h)},${f(yb - h)} L${f(-w / 2 - k * h)},${f(yb - h)} Z`);
    body.setAttribute("stroke-width", f(2 * r));

    // face
    const t = h * 0.72;
    const ex = -k * t;
    const ey = yb - t;
    const sp = Math.min(w * 0.22, 7.2);
    const es = M.eyeS * clamp(w / 26, 0.55, 1);
    const ry = M.expr === "surprised" ? 5 : 4.3;
    const rx = M.expr === "surprised" ? 3.7 : 3.1;
    eoL.setAttribute("rx", String(rx)); eoR.setAttribute("rx", String(rx));
    eoL.setAttribute("ry", f(ry * M.blink)); eoR.setAttribute("ry", f(ry * M.blink));
    eyeL.setAttribute("transform", `translate(${f(ex - sp + M.look.x)} ${f(ey + M.look.y)}) scale(${f(es, 3)})`);
    eyeR.setAttribute("transform", `translate(${f(ex + sp + M.look.x)} ${f(ey + M.look.y)}) scale(${f(es, 3)})`);
    mouth.setAttribute("transform", `translate(${f(ex + k * 9 + M.look.x * 0.6)} ${f(ey + 9 + M.look.y * 0.4)}) scale(${f(es, 3)})`);
    blushL.setAttribute("cx", f(ex - sp - 4.4 + k * 5)); blushL.setAttribute("cy", f(ey + 5.5));
    blushR.setAttribute("cx", f(ex + sp + 4.4 + k * 5)); blushR.setAttribute("cy", f(ey + 5.5));
    blushL.setAttribute("opacity", f(M.blush * M.eyeS)); blushR.setAttribute("opacity", f(M.blush * M.eyeS));

    // arms
    const ts = h * 0.42;
    const shx = -k * ts, shy = yb - ts;
    const off = w / 2 + r * 0.55 - 1;
    const len = 14.5 * M.limbS;
    const lx = shx - off, rxs = shx + off;
    const hlx = lx + len * Math.cos(rad(M.armL)), hly = shy + len * Math.sin(rad(M.armL));
    const hrx = rxs + len * Math.cos(rad(M.armR)), hry = shy + len * Math.sin(rad(M.armR));
    armLEl.setAttribute("x1", f(lx)); armLEl.setAttribute("y1", f(shy)); armLEl.setAttribute("x2", f(hlx)); armLEl.setAttribute("y2", f(hly));
    armREl.setAttribute("x1", f(rxs)); armREl.setAttribute("y1", f(shy)); armREl.setAttribute("x2", f(hrx)); armREl.setAttribute("y2", f(hry));
    handLEl.setAttribute("cx", f(hlx)); handLEl.setAttribute("cy", f(hly));
    handREl.setAttribute("cx", f(hrx)); handREl.setAttribute("cy", f(hry));
    const limbO = f(M.limbS);
    armLEl.style.opacity = limbO; armREl.style.opacity = limbO; handLEl.style.opacity = limbO; handREl.style.opacity = limbO;

    // legs; walking swings the feet front and back and lifts them in turn
    const lgx = Math.min(w * 0.22, 7);
    const lsp = M.legSplay;
    const legO = clamp(leg / 4, 0, 1) * M.limbS;
    const sw = M.walkAmp * Math.sin(M.walkPh);
    const fxL = -lgx - lsp + sw * 4.5, fxR = lgx + lsp - sw * 4.5;
    const fyL = -Math.max(0, sw) * 2.6, fyR = -Math.max(0, -sw) * 2.6;
    legL.setAttribute("x1", f(-lgx)); legL.setAttribute("y1", f(-leg)); legL.setAttribute("x2", f(fxL)); legL.setAttribute("y2", f(fyL));
    legR.setAttribute("x1", f(lgx)); legR.setAttribute("y1", f(-leg)); legR.setAttribute("x2", f(fxR)); legR.setAttribute("y2", f(fyR));
    footL.setAttribute("x1", f(fxL - 3)); footL.setAttribute("y1", f(fyL)); footL.setAttribute("x2", f(fxL + 2)); footL.setAttribute("y2", f(fyL));
    footR.setAttribute("x1", f(fxR - 2)); footR.setAttribute("y1", f(fyR)); footR.setAttribute("x2", f(fxR + 3)); footR.setAttribute("y2", f(fyR));
    legs.style.opacity = f(legO);

    // costumes
    const ps = f(M.propS, 3);
    headG.setAttribute("transform", `translate(${f(-k * h)} ${f(yb - h - r + 2.5)}) scale(${ps})`);
    chestG.setAttribute("transform", M.chest === "envelope"
      ? `translate(${f(-k * h * 0.17)} ${f(yb - h * 0.17)}) scale(${ps})`
      : `translate(${f(-k * h * 0.34)} ${f(yb - h * 0.34)}) scale(${ps})`);
    handG.setAttribute("transform", `translate(${f(hrx)} ${f(hry)}) rotate(${f(M.armR + 90, 1)}) scale(${ps})`);

    // screen-space helpers for the bubble and the hit box
    const hatH = M.head ? 18 : 0;
    head = { x: M.vx + S * sx * (-k * h), y: M.vy + S * sy * (yb - h - r - hatH) };
    const minX = -w / 2 - k * h - r - 6, maxX = w / 2 + r + 6;
    const minY = yb - h - r - 4;
    box = { l: M.vx + minX * S, t: M.vy + minY * S, w: (maxX - minX) * S, h: -minY * S };
  }

  /* ---------- speech ---------- */
  let sayToken = 0;
  let hideTimer = 0;
  function say(text: string, { acts = [], hold, think = false, node, wide = false }: { acts?: Act[]; hold?: number; think?: boolean; node?: HTMLElement; wide?: boolean } = {}) {
    const my = ++sayToken;
    clearTimeout(hideTimer);
    bubble.hidden = false;
    bubble.classList.toggle("wide", wide);
    bActs.innerHTML = "";
    requestAnimationFrame(() => { if (my === sayToken) bubble.classList.add("show"); });
    if (think) {
      bText.innerHTML = `<span class="dots">Thinking</span>`;
      return;
    }
    // the untyped rest stays in the layout, invisible, so the bubble opens at its final size instead of growing (and jumping up) as A types
    const typed = document.createTextNode("");
    const rest = document.createElement("span");
    rest.className = "b-rest";
    bText.replaceChildren(typed, rest);
    let i = 0;
    const type = () => {
      if (my !== sayToken || !alive) return;
      i = RM ? text.length : Math.min(text.length, i + 2);
      typed.data = text.slice(0, i);
      rest.textContent = text.slice(i);
      if (i < text.length) later(24, type);
      else {
        acts.forEach((a) => {
          const b = document.createElement("button");
          b.type = "button";
          b.textContent = a.label;
          if (a.pri) b.className = "pri";
          b.addEventListener("click", (e) => { e.stopPropagation(); a.run(); });
          bActs.appendChild(b);
        });
        if (node) {
          bActs.appendChild(node);
          node.querySelector<HTMLElement>("input:not([type=radio])")?.focus({ preventScroll: true });
        }
        const h = hold ?? (acts.length || node ? 0 : 3200 + text.length * 38);
        if (h) hideTimer = later(h, () => { if (my === sayToken) hideBubble(); });
      }
    };
    type();
  }
  function hideBubble() {
    sayToken++;
    bubble.classList.remove("show");
    clearTimeout(hideTimer);
    hideTimer = later(220, () => { if (!bubble.classList.contains("show")) bubble.hidden = true; });
  }
  function placeBubble() {
    if (bubble.hidden) return;
    // phones: a full-width subtitle docked to the screen edge farther from A, so it never sits on the text being read
    const dock = small();
    bubble.classList.toggle("dock", dock);
    if (dock) {
      const bh = bubble.offsetHeight;
      const top = head.y > innerHeight * 0.5 ? 72 : innerHeight - bh - 14;
      bubble.style.transform = `translate(12px, ${Math.round(top)}px)`;
      return;
    }
    const bw = bubble.offsetWidth, bh = bubble.offsetHeight;
    const right = head.x > innerWidth * 0.52;
    let left = right ? head.x - bw + 28 : head.x - 28;
    let top = head.y - bh - 12;
    if (top < 70) {
      top = clamp(head.y + 10, 70, innerHeight - bh - 12);
      left = right ? box.l - bw - 10 : box.l + box.w + 10;
    }
    left = clamp(left, 12, innerWidth - bw - 12);
    top = clamp(top, 64, innerHeight - bh - 10);
    bubble.style.transform = `translate(${left.toFixed(0)}px, ${top.toFixed(0)}px)`;
  }

  /* ---------- jumps ---------- */
  let J: Jump | null = null;
  function jumpTo(fn: () => Pt, o: JumpOpts) {
    if (!o.keepBubble) hideBubble();
    if (o.shape) M.shpT = { ...o.shape };
    const to = fn();
    const toS = o.scale ?? restScale();
    if (RM) {
      M.vx = to.x; M.vy = to.y; M.s = M.sT = toS;
      M.shp = { ...M.shpT };
      M.mode = o.next; M.slotEl = o.slotEl ?? null;
      o.onLand?.();
      return;
    }
    const dist = Math.hypot(to.x - M.vx, to.y - M.vy);
    J = {
      fn, fromX: M.vx, fromY: M.vy, fromS: M.s, toS,
      t: 0, crouch: o.crouch ?? 0.13,
      dur: o.dur ?? clamp(0.4 + dist / 1900, 0.45, 0.95),
      arc: o.arc ?? clamp(40 + dist * 0.26, 50, 210),
      spin: o.spin ?? 0, next: o.next, slotEl: o.slotEl ?? null, onLand: o.onLand,
    };
    M.mode = "jump";
    M.sqT = 0.8;
  }
  const jumpP = (fn: () => Pt, o: JumpOpts) => new Promise<void>((res) => jumpTo(fn, { ...o, onLand: () => { o.onLand?.(); res(); } }));
  function stepJump(dt: number) {
    const j = J!;
    j.t += dt;
    const to = j.fn();
    if (j.t < j.crouch) {
      M.vx = j.fromX; M.vy = j.fromY;
      M.sqT = 0.78;
      M.pose = "rest";
      return;
    }
    const u = clamp((j.t - j.crouch) / j.dur, 0, 1);
    const e = ease(u);
    M.vx = lerp(j.fromX, to.x, e);
    M.vy = lerp(j.fromY, to.y, u) - j.arc * 4 * u * (1 - u);
    M.s = lerp(j.fromS, j.toS, e);
    M.spin = j.spin * e;
    M.sqT = u < 0.85 ? 1.14 : 1;
    M.pose = "air";
    M.legSplay = 3;
    if (u >= 1) {
      J = null;
      M.spin = 0; M.sqT = 1; M.sqV = -5.5; M.legSplay = 0;
      M.s = M.sT = j.toS;
      M.mode = j.next; M.slotEl = j.slotEl;
      M.pose = M.scenePose;
      j.onLand?.();
    }
  }

  /* ---------- scenes: read straight off the page ---------- */
  const seen = new Set<string>();
  const sceneEl = (id: string | null) => (id ? sceneEls.find((s) => s.dataset.scene === id) : undefined);
  function enterScene(id: string | null) {
    const d = sceneEl(id)?.dataset;
    if (!id || !d) return;
    setCostume({ head: d.head || null, chest: d.chest || null, hand: d.hand || null });
    M.scenePose = d.pose || "rest";
    M.sceneExpr = d.expr || "normal";
    M.pose = M.scenePose;
    if (!sleeping) M.expr = M.sceneExpr;
    const key = `${path}:${id}`;
    if (seen.has(key)) return;
    if (d.intro !== undefined) {
      seen.add(key);
      void heroIntroTalk();
    } else if (d.line) {
      seen.add(key);
      say(d.line);
    }
  }
  async function heroIntroTalk() {
    M.expr = "surprised";
    say("Oh! Hi. You caught me.", { hold: 2300 });
    await wait(900);
    M.expr = "happy";
    await wait(1500);
    if (M.sceneId !== "hero" || M.mode === "jump") return;
    say("I'm A, the yellow one from Yassine's logo. He's busy building things, so I do the introductions.", {
      acts: [
        { label: "Introduce him", pri: true, run: () => { hideBubble(); scrollToId("about"); } },
        { label: "I'll look around", run: () => say("Fair. Click me whenever you need me. You can also drag me.", { hold: 3600 }) },
      ],
    });
  }
  function scrollToId(id: string, block: ScrollLogicalPosition = "start") {
    document.getElementById(id)?.scrollIntoView({ behavior: RM ? "auto" : "smooth", block });
  }

  function perchEl(el: HTMLElement): Element | null {
    if (M.pin && M.pin.scene === el.dataset.scene) return M.pin.el;
    return el.dataset.perch ? $(el.dataset.perch) : null;
  }
  function sceneAt(): HTMLElement | null {
    const y = innerHeight * 0.45;
    let found: HTMLElement | null = null;
    for (const el of sceneEls) {
      const r = el.getBoundingClientRect();
      if (r.top <= y && r.bottom > y) found = el;
    }
    return found;
  }
  /** A perch marked data-fit sits beside type that scales with the viewport: A
      shrinks with it so it never grows taller than the words it stands next to. */
  function perchScale(pe: Element) {
    const base = restScale();
    if (!(pe as HTMLElement).dataset.fit) return base;
    const fs = parseFloat(getComputedStyle(pe.parentElement ?? pe).fontSize) || 62;
    return clamp(fs / 62, Math.min(0.85, base), base);
  }
  function targetFor(el: HTMLElement): Want {
    const pe = perchEl(el);
    const id = el.dataset.scene!;
    if (pe) {
      const p = perchPos(pe);
      const lo = M.kind === "perch" && M.sceneId === id ? 128 : 150;
      if (p.y > lo && p.y < innerHeight - 20 && p.x > 24 && p.x < innerWidth - 24) return { kind: "perch", id, fn: () => perchPos(pe), scale: perchScale(pe) };
    }
    return { kind: "float", id, fn: floatPos, scale: restScale() };
  }
  let pend: { key: string; since: number } | null = null;
  function watchScenes(now: number) {
    if (transitioning) return;
    const el = sceneAt();
    if (!el) return;
    const want = targetFor(el);
    if (want.id === M.sceneId && want.kind === M.kind) { pend = null; return; }
    const key = want.id + want.kind;
    if (!pend || pend.key !== key) { pend = { key, since: now }; return; }
    if (now - pend.since < 170) return;
    pend = null;
    jumpTo(want.fn, { scale: want.scale,
      next: want.kind, shape: REST,
      onLand: () => {
        const changed = M.sceneId !== want.id;
        M.sceneId = want.id; M.kind = want.kind;
        if (changed) { M.pin = null; enterScene(want.id); }
      },
    });
  }
  const landHere = (want: Want, after?: () => void) => () => {
    M.sceneId = want.id; M.kind = want.kind;
    after?.();
  };

  /* ---------- input: look, pet, idle, drag, click ---------- */
  let px = -1, py = -1, lastInput = performance.now(), sleeping = false;
  let hoverSince = 0;
  const wake = () => {
    lastInput = performance.now();
    if (sleeping) {
      sleeping = false;
      zzz.classList.remove("on");
      M.expr = "surprised";
      M.sqV = -4;
      say("I wasn't sleeping. I was resting my eyes.", { hold: 2600 });
      later(900, () => { if (!sleeping) M.expr = M.sceneExpr; });
    } else if (M.expr === "sleepy") {
      M.expr = M.sceneExpr;
    }
  };
  on(window, "pointermove", (e: PointerEvent) => { px = e.clientX; py = e.clientY; wake(); }, { passive: true });
  on(window, "scroll", () => {
    wake();
    if (started && !introDone && scrollY > 40 && M.mode === "slot") popOut();
  }, { passive: true });
  on(window, "keydown", wake);

  let drag: { x0: number; y0: number; moved: boolean; lastX: number; tx?: number; ty?: number } | null = null;
  on(hit, "pointerdown", (e: PointerEvent) => {
    if (M.mode === "jump" || transitioning || gameOn) return;
    hit.setPointerCapture(e.pointerId);
    drag = { x0: e.clientX, y0: e.clientY, moved: false, lastX: e.clientX };
  });
  on(hit, "pointermove", (e: PointerEvent) => {
    if (!drag) return;
    if (!drag.moved && Math.hypot(e.clientX - drag.x0, e.clientY - drag.y0) > 7 && ["perch", "float", "stage"].includes(M.mode) && !performing) {
      drag.moved = true;
      M.mode = "drag";
      M.expr = "surprised";
      M.pose = "drag";
      hideBubble();
    }
    if (drag.moved) {
      drag.tx = e.clientX + box.w * 0.12;
      drag.ty = e.clientY + box.h * 0.82;
      M.rotT = clamp((e.clientX - drag.lastX) * 1.6, -28, 28);
      drag.lastX = e.clientX;
    }
  });
  const endDrag = () => {
    if (!drag) return;
    const d = drag;
    drag = null;
    if (d.moved) {
      M.rotT = 0;
      M.expr = "dizzy";
      const el = sceneAt() || sceneEls[0];
      if (!el) return;
      const want = targetFor(el);
      jumpTo(want.fn, { scale: want.scale,
        next: want.kind, shape: REST, spin: 360, arc: 120,
        onLand: landHere(want, () => {
          say(seen.has("thrown") ? "Again? Fine. Wheee." : "Wheee. Okay, that was fun. Again?", { hold: 2600 });
          seen.add("thrown");
          later(1300, () => (M.expr = M.sceneExpr));
        }),
      });
    } else {
      onClickA();
    }
  };
  on(hit, "pointerup", endDrag);
  on(hit, "pointercancel", () => { if (drag && drag.moved) endDrag(); else drag = null; });
  on(hit, "keydown", (e: KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClickA(); } });
  on(hit, "pointerenter", () => (hoverSince = performance.now()));
  on(hit, "pointerleave", () => (hoverSince = 0));

  function onClickA() {
    if (gameOn) return;
    hoverSince = 0; // a click is not petting
    if (transitioning) return;
    if (!introDone) return popOut();
    if (M.mode === "slot" && M.slotEl === navLogo) return comeOut();
    if (performing || M.mode === "jump" || M.mode === "slot") return;
    M.sqV = -5;
    const touch = sceneEl(M.sceneId)?.dataset.touch;
    const tk = `touch:${path}:${M.sceneId}`;
    if (touch && !seen.has(tk)) {
      seen.add(tk);
      M.expr = "surprised";
      later(900, () => (M.expr = M.sceneExpr));
      return say(touch, { hold: 2200 });
    }
    menu();
  }
  function goTo(href: string) {
    hideBubble();
    if (!api.navigate(href)) opts.push(href);
  }
  /* ---------- the game: Catch A, played on the page itself ----------
     A explains the rules in its bubble and demonstrates them, then hops
     across the real page for ten seconds. The hops come from a seed the
     server signed; the server replays the clicks before it hands out a
     prize code (src/lib/game.ts, /api/game/*). */
  let gameReady = false;
  let gameOn = false; // from the rules to the result
  let playing = false; // the ten seconds
  const bornAt = performance.now();
  let offered = (() => {
    try { return sessionStorage.getItem("a-game-offered") === "1"; } catch { return false; }
  })();
  const gField = $("#gField")!, gHud = $("#gHud")!, gBar = $("#gBar")!, gScore = $("#gScore")!;
  const twin = $<SVGSVGElement>("#aTwin")!;
  const peeking = () => outerWidth - innerWidth > 170 || outerHeight - innerHeight > 230;
  const today = () => new Date().toISOString().slice(0, 10);
  const triesUsed = () => {
    try {
      const t = JSON.parse(localStorage.getItem("a-game-tries") ?? "{}");
      return t.day === today() ? Number(t.n) || 0 : 0;
    } catch { return 0; }
  };
  const spendTry = () => {
    try { localStorage.setItem("a-game-tries", JSON.stringify({ day: today(), n: triesUsed() + 1 })); } catch { /* the server still checks */ }
  };
  const triesLeft = () => Math.max(0, GAME.triesPerDay - triesUsed());
  /** A's size in the game: its body matches the server's hit box. */
  const gScale = () => clamp((2 * GAME.hitH * Math.min(innerWidth, innerHeight)) / 68, 0.5, 2.6);
  /** A spot (fractions of the window) → where A's feet go so its body is centred on it. */
  const spotPt = (x: number, y: number) => (): Pt => {
    const sc = gScale();
    return { x: x * innerWidth + 8.7 * sc, y: y * innerHeight + 43 * sc };
  };
  const hopTo = (x: number, y: number, o: Partial<JumpOpts> = {}) =>
    jumpP(spotPt(x, y), { next: "game", shape: REST, scale: gScale(), ...o });
  function showTwin(x: number | null, y = 0) {
    if (x === null) { twin.classList.remove("on", "hurt"); return; }
    const sc = gScale();
    twin.style.width = `${(64 * sc).toFixed(0)}px`;
    twin.style.height = `${(92 * sc).toFixed(0)}px`;
    twin.style.transform = `translate(${(x * innerWidth - 25.3 * sc).toFixed(0)}px, ${(y * innerHeight - 41 * sc).toFixed(0)}px)`;
    twin.classList.add("on");
  }
  function lockPage(on: boolean) {
    root.style.overflow = on ? "hidden" : "";
    gField.hidden = !on;
    gHud.hidden = !on;
  }
  function pop(text: string, x: number, y: number) {
    const el = document.createElement("p");
    el.className = "g-pop";
    el.textContent = text;
    el.style.transform = `translate(${x.toFixed(0)}px, ${y.toFixed(0)}px)`;
    document.body.appendChild(el);
    later(800, () => el.remove());
  }
  const comeBack = "Fine. Click me if you change your mind.";

  function offerGame() {
    try { sessionStorage.setItem("a-game-offered", "1"); } catch { /* private mode */ }
    M.expr = "happy";
    say("Psst. Want to play a game? Nobody has ever caught me. Don't tell Yassine I asked.", {
      acts: [
        { label: "Let's play", pri: true, run: () => void gameIntro() },
        { label: "Not now", run: () => say(comeBack, { hold: 2600 }) },
      ],
    });
  }

  async function gameIntro() {
    if (gameOn || performing || transitioning || !gameReady || !introDone || M.mode === "slot") return;
    gameOn = true;
    hideBubble();
    sleeping = false;
    zzz.classList.remove("on");
    setCostume({});
    M.pin = null;
    await hopTo(0.5, 0.5, { arc: 160, dur: 0.6 });
    if (!gameOn) return;
    M.pose = "wave";
    M.expr = "happy";
    if (peeking()) return gamePeek();
    if (triesLeft() <= 0) return gameStop("That's enough games for today. Come back tomorrow, I'll be practising.");
    say(`Here's the deal. I'll pop up all over this page for 10 seconds. Catch me ${GAME.need} times.`, {
      acts: [
        { label: "Show me", pri: true, run: () => void gameDemo() },
        { label: "Not now", run: () => gameStop(comeBack) },
      ],
    });
  }

  async function gameDemo() {
    hideBubble();
    M.expr = "happy";
    for (const [x, y] of [[0.28, 0.36], [0.72, 0.62], [0.42, 0.5]]) {
      await hopTo(x, y, { arc: 110, dur: 0.36, crouch: 0.05 });
      if (!gameOn) return;
      await wait(240);
    }
    showTwin(0.66, 0.5);
    M.pose = "point";
    M.expr = "surprised";
    later(900, () => { if (gameOn && !playing) M.expr = "normal"; });
    say("Careful. That grey one is my evil twin. Catch him and you lose a point.", {
      acts: [{ label: "Got it", pri: true, run: gameTerms }],
    });
  }

  function gameTerms() {
    showTwin(null);
    M.pose = "hips";
    M.expr = "happy";
    const left = triesLeft();
    say(
      `I get faster as we go. Much faster. Nobody has beaten me yet, so if you do, something good happens. ${left} ${left === 1 ? "try" : "tries"} left today. Ready?`,
      {
        wide: true,
        acts: [
          { label: "Let's go!", pri: true, run: () => void gamePlay() },
          { label: "Maybe later", run: () => gameStop(comeBack) },
        ],
      },
    );
  }

  let G: { ticket: string; seed: number; spots: Spot[]; clicks: Click[]; t0: number; k: number; hits: number; peekAt: number } | null = null;

  async function gamePlay() {
    if (peeking()) return gamePeek();
    if (triesLeft() <= 0) return gameStop("That's enough games for today. Come back tomorrow, I'll be practising.");
    M.pose = "rest";
    say("", { think: true });
    let data: { ticket: string; seed: number; error?: string };
    try {
      const r = await fetch("/api/game/start", { method: "POST" });
      data = await r.json();
      if (!r.ok) throw new Error(data.error);
    } catch (e) {
      return gameStop((e as Error).message || "The game is resting right now. Try again later.");
    }
    if (!gameOn) return;
    spendTry();
    G = { ticket: data.ticket, seed: data.seed, spots: schedule(data.seed), clicks: [], t0: 0, k: -2, hits: 0, peekAt: 0 };
    lockPage(true);
    gScore.textContent = `0 / ${GAME.need}`;
    gBar.style.transform = "scaleX(1)";
    await hopTo(0.5, 0.5, { dur: 0.3, arc: 60 });
    for (const n of ["3…", "2…", "1…"]) {
      say(n, { hold: 0 });
      M.sqV = -4;
      await wait(650);
      if (!gameOn || !G) return;
    }
    say("Go!", { hold: 450 });
    M.expr = "happy";
    G.t0 = performance.now();
    playing = true;
  }

  /** Runs inside the frame loop: hop on schedule, show the twin, end on time. */
  function stepGame(now: number) {
    const g = G;
    if (!g) return;
    const t = now - g.t0;
    gBar.style.transform = `scaleX(${clamp(1 - t / GAME.duration, 0, 1).toFixed(4)})`;
    if (t - g.peekAt > 300) {
      g.peekAt = t;
      if (peeking()) {
        playing = false;
        G = null;
        lockPage(false);
        showTwin(null);
        M.alphaT = 1;
        void hopTo(0.5, 0.5, { dur: 0.4 }).then(() => gameOn && gamePeek(true));
        return;
      }
    }
    if (t >= GAME.duration) {
      playing = false;
      void gameFinish();
      return;
    }
    // A teleports: it vanishes between spots and pops up on the next one, so
    // nobody can follow it in flight and be waiting where it lands
    const i = g.spots.findIndex((sp) => t >= sp.t0 && t <= sp.t1);
    if (i !== g.k) {
      g.k = i;
      const sp = i >= 0 ? g.spots[i] : null;
      if (!sp || sp.decoy) {
        M.alpha = M.alphaT = 0;
        if (sp) showTwin(sp.x, sp.y);
        else showTwin(null);
      } else {
        showTwin(null);
        const p = spotPt(sp.x, sp.y)();
        J = null;
        M.mode = "game";
        M.vx = p.x;
        M.vy = p.y;
        M.s = M.sT = gScale();
        M.shp = { ...REST };
        M.alpha = M.alphaT = 1;
        M.sq = 0.72;
        M.sqV = 7;
        M.expr = "happy";
        M.pose = ["wave", "air", "hips", "point"][i % 4];
      }
    }
  }

  on(gField, "pointerdown", (e: PointerEvent) => {
    const g = G;
    if (!playing || !g) return;
    e.preventDefault();
    g.clicks.push({ t: Math.round(performance.now() - g.t0), x: e.clientX / innerWidth, y: e.clientY / innerHeight });
    const { hits } = score(g.seed, g.clicks, innerWidth / innerHeight);
    if (hits > g.hits) {
      M.expr = "surprised";
      M.sqV = -7;
      pop("+1", e.clientX, e.clientY);
      later(380, () => { if (playing) M.expr = "happy"; });
    } else if (hits < g.hits) {
      twin.classList.add("hurt");
      pop("−1", e.clientX, e.clientY);
    }
    g.hits = hits;
    gScore.textContent = `${hits} / ${GAME.need}`;
  });
  on(window, "keydown", (e: KeyboardEvent) => {
    if (gameOn && e.key === "Escape") gameStop();
  });

  async function gameFinish() {
    const g = G;
    lockPage(false);
    showTwin(null);
    M.alphaT = 1;
    if (!g) return;
    await hopTo(0.5, 0.45, { arc: 140, dur: 0.5 });
    say("", { think: true });
    let r: { hits: number; win: boolean; id: string };
    try {
      r = await finishCall(g);
    } catch (e) {
      return gameStop((e as Error).message);
    }
    if (!gameOn) return;
    const left = triesLeft();
    const again = left > 0 ? [{ label: `Again (${left} left)`, pri: true, run: () => void gamePlay() }] : [];
    if (r.win) return gameWon(g, r.id);
    M.pose = "tada";
    M.expr = "happy";
    if (r.hits >= GAME.need) {
      say("Too perfect. Nobody catches me that cleanly. That looked like a robot. Try again like a human, I'll be watching.", { acts: [...again, { label: "Done", run: () => gameStop() }] });
    } else {
      say(`Ha. ${r.hits} out of ${GAME.need}. ${r.hits >= GAME.need - 1 ? "So close it scared me." : "I'm quick. It's the legs."}`, {
        acts: [...again, { label: "Done", run: () => gameStop() }],
      });
    }
  }

  async function finishCall(g: NonNullable<typeof G>, extra?: { email: string; prize: string; request: string }) {
    const res = await fetch("/api/game/finish", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ticket: g.ticket, clicks: g.clicks, aspect: innerWidth / innerHeight, ...extra }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Something went wrong. Try again.");
    return data as { hits: number; win: boolean; id: string; code?: string; prize?: string };
  }

  function gameWon(g: NonNullable<typeof G>, id: string) {
    M.expr = "dizzy";
    M.pose = "air";
    later(1100, () => { if (gameOn) { M.expr = "happy"; M.pose = "hips"; } });
    const form = document.createElement("form");
    form.className = "b-form";
    form.noValidate = true;
    form.innerHTML = `
      <label class="b-field"><span>Your request</span><textarea name="request" rows="3" maxlength="600" placeholder="An app idea, a game, a website, a 3D thing…" required></textarea></label>
      <label class="b-field"><span>Your email</span><input name="email" type="email" autocomplete="email" required></label>
      <p class="err" role="alert"></p>
      <button class="pri" type="submit">Tell A</button>`;
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const fd = new FormData(form);
      const email = String(fd.get("email") ?? "").trim();
      const request = String(fd.get("request") ?? "").trim();
      const prize = "discount";
      const err = form.querySelector<HTMLElement>(".err")!;
      if (request.length < 3) { err.textContent = "Tell me what you want first."; return; }
      if (!/^\S+@\S+\.\S+$/.test(email)) { err.textContent = "That email doesn't look complete."; return; }
      err.textContent = "";
      try {
        const r = await finishCall(g, { email, prize, request });
        if (r.code) gameClaimed(id, r.code, prize, email, request);
      } catch (x) {
        err.textContent = (x as Error).message;
      }
    });
    say("No way. You actually caught me. Okay, a deal is a deal: what do you want? Tell me your request.", { node: form, wide: true });
  }

  function gameClaimed(id: string, code: string, prize: string, email: string, request: string) {
    const verify = `${location.origin}/game/verify?${new URLSearchParams({ id, email, prize, code })}`;
    const letter = `Hi Yassine,\n\nI caught A! A said you'd give me a promo or discount on this:\n\n${request}\n\nGame: ${id}\nCode: ${code}\nMy email: ${email}\n\nCheck it here: ${verify}\n`;
    const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`I caught A (code ${code})`)}&body=${encodeURIComponent(letter)}`;
    M.expr = "happy";
    M.pose = "tada";
    say(`Deal. I'll tell Yassine to give you a promo or discount on that. Your code is ${code}. Send it to him from ${email}, the email is already written.`, {
      wide: true,
      acts: [
        { label: "Email it to Yassine", pri: true, run: () => { location.href = mailto; } },
        { label: "Copy the email", run: () => void navigator.clipboard?.writeText(letter).then(() => say(`Copied. Paste it into an email to ${CONTACT_EMAIL}. Code: ${code}.`, { wide: true, acts: [{ label: "Done", run: () => gameStop() }] })) },
        { label: "Done", run: () => gameStop("Congrats. And remember: you didn't hear it from me.") },
      ],
    });
  }

  function gamePeek(midGame = false) {
    M.pose = "think";
    M.expr = "surprised";
    say(
      midGame
        ? "I saw that. No peeking mid-game, that try is gone. Close the inspector and we'll go again."
        : "No peeking. I can see the inspector is open. Close it and I'll play fair. I always do.",
      {
        acts: [
          { label: "I closed it", pri: true, run: () => (peeking() ? gamePeek(midGame) : gameTerms()) },
          { label: "Done", run: () => gameStop() },
        ],
      },
    );
  }

  /** Leave the game from anywhere: A goes back to where the page wants it. */
  function gameStop(line?: string) {
    playing = false;
    G = null;
    lockPage(false);
    showTwin(null);
    M.alphaT = 1;
    hideBubble();
    if (!gameOn) return;
    gameOn = false;
    const el = sceneAt() || sceneEls[0];
    if (!el) return placeHere();
    const want = targetFor(el);
    M.sceneId = null;
    jumpTo(want.fn, {
      scale: want.scale, next: want.kind, shape: REST, arc: 160,
      onLand: landHere(want, () => {
        enterScene(want.id);
        if (line) say(line, { hold: 3000 });
      }),
    });
  }
  function menu() {
    const more = { label: "More", run: menu };
    say("What would you like to know?", {
      acts: [
        { label: "Who is he?", run: () => say("Yassine Amraoui. A creator: you bring an idea, he turns it into the real thing. Apps, games, 3D worlds with AI inside, websites. He designs it and builds it. I'm his only employee.", { acts: [more] }) },
        { label: "Best project?", run: () => say("Warehouse Safety Training. An AI instructor inside a 3D warehouse, correcting trainees in real time. I even have the hat.", { acts: [{ label: "Open the case study", pri: true, run: () => goTo("/work/warehouse-safety-training") }, more] }) },
        { label: "Is he available?", run: () => say("He's taking new projects, remotely, for clients worldwide. Tell me what you're building.", { acts: [{ label: "Start a project", pri: true, run: () => (path === "/" ? (hideBubble(), scrollToId("contact")) : goTo("/#contact")) }, more] }) },
        { label: "How does he work?", run: () => say("Every project goes brief, structure, system, craft, build, shipped. The same person does every step, so nothing gets lost in a handoff.", { acts: [more] }) },
        ...(gameReady ? [{ label: "Play a game?", pri: true, run: () => void gameIntro() }] : []),
        { label: "What are you?", run: () => say("The A from his logo. He drew me, then taught me to walk. Everything I do is code running in this page.", { acts: [more] }) },
      ],
    });
  }

  // page-level clicks: the Ask A pill, the logo when A rests in it, the
  // playground skills and the Look-closer hotspots (all delegated, so they
  // survive route changes)
  on(document, "click", (e: MouseEvent) => {
    if (!started) return;
    const t = e.target as Element | null;
    if (!t?.closest) return;
    if (t.closest("#callA")) {
      e.preventDefault();
      e.stopPropagation();
      if (transitioning) return;
      if (!introDone) return popOut();
      if (M.mode === "slot" && M.slotEl === navLogo) return comeOut();
      if (["perch", "float", "stage"].includes(M.mode)) { M.sqV = -5; menu(); }
      return;
    }
    if (t.closest("#brandLink") && introDone && M.mode === "slot" && M.slotEl === navLogo) {
      e.preventDefault();
      e.stopPropagation();
      comeOut();
      return;
    }
    const skill = t.closest<HTMLElement>("[data-skill]");
    if (skill) return void perform(skill.dataset.skill!, skill);
    const hs = t.closest<HTMLElement>("[data-hs]");
    if (hs) pinTo(Number(hs.dataset.hs));
  }, true);
  on(document, "pointerover", (e: PointerEvent) => {
    const row = (e.target as Element | null)?.closest?.<HTMLElement>(".c-hs-list [data-hs]");
    if (row) $$(".hs")[Number(row.dataset.hs)]?.classList.add("on");
  });
  on(document, "pointerout", (e: PointerEvent) => {
    const row = (e.target as Element | null)?.closest?.<HTMLElement>(".c-hs-list [data-hs]");
    if (row && !row.classList.contains("on")) $$(".hs")[Number(row.dataset.hs)]?.classList.remove("on");
  });

  function pinTo(i: number) {
    const boxes = $$(".hs");
    const rows = $$(".c-hs-list [data-hs]");
    const target = boxes[i];
    if (!target || transitioning || performing || !["perch", "float"].includes(M.mode)) return;
    boxes.forEach((b, k) => b.classList.toggle("on", k === i));
    rows.forEach((r, k) => r.classList.toggle("on", k === i));
    M.pin = { scene: "c-look", el: target };
    jumpTo(() => perchPos(target), {
      next: "perch", shape: REST,
      onLand: () => {
        M.sceneId = "c-look"; M.kind = "perch";
        M.pose = "point";
        say(target.dataset.note ?? "", { hold: 5600 });
      },
    });
  }

  /* ---------- intro: the logo wakes up ---------- */
  let introDone = false;
  function popOut() {
    if (introDone) return;
    introDone = true;
    setState("out");
    M.eyeST = 1; M.limbST = 1;
    const el = sceneAt() || sceneEls[0];
    if (!el) return;
    const want = targetFor(el);
    jumpTo(want.fn, { scale: want.scale, next: want.kind, shape: REST, arc: 170, dur: 0.8, onLand: landHere(want, () => enterScene(want.id)) });
  }
  async function intro() {
    const logo = bigLogo || navLogo;
    if (!logo) return;
    setState(bigLogo ? "big" : "navintro");
    M.slotEl = logo;
    M.mode = "slot";
    const sl = slotFor(logo);
    M.vx = sl.x; M.vy = sl.y; M.s = M.sT = sl.s;
    await wait(700);
    if (introDone) return;
    for (const r of [5, -5, 4, -3, 0]) { M.rotT = r; await wait(130); }
    await wait(350);
    if (introDone) return;
    M.eyeST = 1;
    await wait(450);
    blinkNow(); await wait(260); blinkNow();
    await wait(500);
    if (introDone) return;
    M.shpT = { ...LOGO, k: 0.2 };
    M.sqV = -3;
    await wait(420);
    popOut();
  }

  /* ---------- A carries the letter up into the nav logo, and comes back ---------- */
  function goHome() {
    if (!navLogo) return;
    const nl = navLogo;
    setCostume({ chest: "envelope" });
    M.pose = "hold";
    const sl = () => slotFor(nl);
    jumpTo(sl, {
      next: "slot", slotEl: nl, shape: LOGO, scale: sl().s, arc: 190, dur: 0.9,
      onLand: () => {
        setCostume({});
        M.limbST = 0;
        setState("nav");
        later(900, () => { if (M.mode === "slot") M.eyeST = 0; });
      },
    });
  }
  function comeOut() {
    setState("out");
    M.eyeST = 1; M.limbST = 1;
    const el = sceneAt() || sceneEls[0];
    if (!el) return;
    const want = targetFor(el);
    jumpTo(want.fn, { scale: want.scale,
      next: want.kind, shape: REST, arc: 150,
      onLand: landHere(want, () => {
        enterScene(want.id);
        say("You called?", { acts: [{ label: "Ask something", pri: true, run: menu }], hold: 5000 });
      }),
    });
  }

  /* ---------- playground ---------- */
  let performing = false;
  const stageSpot = (fx = 0.5) => (): Pt => {
    const r = (stagePerchEl ?? document.body).getBoundingClientRect();
    return { x: r.left + r.width * fx, y: r.top };
  };
  function highlight(s: string) {
    const e = s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]!);
    if (e.trim().startsWith("//")) return `<span class="c">${e}</span>`;
    // strings light up while they are still being typed
    return e.replace(/"[^"]*"?/g, (m) => `<span class="s">${m}</span>`);
  }
  function typeCode(code: HTMLElement, lines: string[], onLine: (i: number) => void) {
    return new Promise<void>((resolve) => {
      let li = 0, ci = 0, out = "";
      const tick = () => {
        if (li >= lines.length) { code.innerHTML = out + `<span class="caret"></span>`; return resolve(); }
        const line = lines[li];
        ci = RM ? line.length : ci + 1;
        code.innerHTML = out + highlight(line.slice(0, ci)) + `<span class="caret"></span>`;
        if (ci >= line.length) {
          out += highlight(line) + "\n";
          onLine(li);
          li++; ci = 0;
          later(RM ? 60 : 850, tick);
        } else later(42, tick);
      };
      tick();
    });
  }
  /** One line typed into an element at reading speed. */
  function typeInto(el: HTMLElement, text: string, cps = 36) {
    return new Promise<void>((resolve) => {
      let i = 0;
      const tick = () => {
        i = RM ? text.length : i + 1;
        el.textContent = text.slice(0, i);
        if (i < text.length) later(1000 / cps, tick);
        else resolve();
      };
      tick();
    });
  }

  // the caption under the stage: what A is doing, step by step, at the visitor's pace
  let capBtn: HTMLElement | null = null;
  const capItems = () => $$<HTMLElement>("#cap [data-cap]");
  function showCap(skill: string) {
    capItems().forEach((c) => {
      const on = c.dataset.cap === skill;
      c.hidden = !on;
      c.classList.toggle("in", on);
      if (on) $$("li", c).forEach((li) => li.classList.remove("now", "past"));
    });
  }
  /** Light beat i in the caption and fill the pressed button up to it. */
  function beat(i: number) {
    const item = capItems().find((c) => !c.hidden);
    const lis = item ? $$("li", item) : [];
    lis.forEach((li, k) => { li.classList.toggle("past", k < i); li.classList.toggle("now", k === i); });
    const p = i >= lis.length ? 100 : ((i + 0.5) / Math.max(lis.length, 1)) * 100;
    capBtn?.style.setProperty("--p", `${Math.round(p)}%`);
  }

  /**
   * Games: obstacles slide in along the stage floor and A hops over them.
   * `auto` = A plays itself; otherwise the visitor jumps A (click the stage,
   * Space or Up). Resolves with how many obstacles were cleared.
   */
  function runner(run: HTMLElement, hud: HTMLElement, n: number, auto: boolean, spot: () => Pt, fx: number) {
    return new Promise<number>((resolve) => {
      const stage = run.parentElement!;
      const W = stage.clientWidth;
      const speed = W / 2.5;
      const ow = 22, hw = 16, ax = W * fx;
      type Ob = { x: number; el: HTMLElement; hit: boolean; judged: boolean };
      const obs: Ob[] = [];
      let spawned = 0, score = 0, nextAt = 0.5, t = 0, last = performance.now();
      const hop = () => {
        if (M.mode !== "stage") return;
        jumpTo(spot, { next: "stage", shape: REST, arc: 95, dur: 0.52, crouch: 0.04, keepBubble: true });
      };
      const hudText = () => (hud.textContent = auto ? "A is playing" : `Your turn · ${score} / ${n}`);
      hudText();
      const onKey = (e: KeyboardEvent) => {
        if (e.code === "Space" || e.key === "ArrowUp") { e.preventDefault(); hop(); }
      };
      const onPtr = (e: PointerEvent) => {
        const r = stage.getBoundingClientRect();
        if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) hop();
      };
      if (!auto) {
        window.addEventListener("keydown", onKey);
        document.addEventListener("pointerdown", onPtr, true);
      }
      const finish = () => {
        window.removeEventListener("keydown", onKey);
        document.removeEventListener("pointerdown", onPtr, true);
        obs.forEach((o) => o.el.remove());
        resolve(score);
      };
      const step = () => {
        if (!alive || !stagePerchEl?.isConnected) return finish();
        const now = performance.now();
        const dt = Math.min(0.05, (now - last) / 1000);
        last = now;
        t += dt;
        if (spawned < n && t >= nextAt) {
          const el = document.createElement("i");
          el.className = "obst";
          run.appendChild(el);
          obs.push({ x: W + 10, el, hit: false, judged: false });
          spawned++;
          nextAt = t + (auto ? 1.5 : 1.15 + Math.random() * 0.7);
        }
        const lift = stagePerchEl.getBoundingClientRect().top - M.vy;
        for (const o of obs) {
          o.x -= speed * dt;
          o.el.style.transform = `translateX(${o.x.toFixed(1)}px)`;
          const overlap = o.x < ax + hw && o.x + ow > ax - hw;
          if (overlap && !o.hit && lift < 24) {
            o.hit = true;
            o.el.classList.add("hit");
            M.expr = "dizzy";
            later(700, () => { if (M.expr === "dizzy") M.expr = "normal"; });
          }
          if (!o.judged && o.x + ow < ax - hw) {
            o.judged = true;
            if (!o.hit) { score++; o.el.classList.add("ok"); }
            hudText();
          }
          if (auto && !o.judged && o.x > ax && o.x - (ax + hw) < speed * 0.14) hop();
        }
        for (let k = obs.length - 1; k >= 0; k--) if (obs[k].x < -ow - 10) { obs[k].el.remove(); obs.splice(k, 1); }
        if (spawned >= n && obs.length === 0) return finish();
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  }

  const blockKeys = (e: KeyboardEvent) => { if (e.code === "Space" || e.key === "ArrowUp") e.preventDefault(); };
  async function perform(skill: string, btn: HTMLElement) {
    if (performing || transitioning) return;
    const stage = $("#stage"), code = $("#code"), a3d = $("#a3d");
    const ux = $("#ux"), run = $("#run"), hud = $("#runHud"), chat = $("#chat");
    if (!stage || !code || !a3d || !stageLogo || !ux || !run || !hud || !chat) return;
    const inStage = () => M.sceneId === "play" && (M.mode === "perch" || M.mode === "float");
    if (!inStage()) {
      // bring the stage into view; A walks over on its own, and the show starts when it lands
      stage.scrollIntoView({ behavior: RM ? "auto" : "smooth", block: "center" });
      const t0 = performance.now();
      while (!inStage()) {
        if (!alive || performance.now() - t0 > 4500) return;
        await wait(150);
      }
      if (performing || transitioning) return;
    }
    const logo = stageLogo;
    performing = true;
    capBtn = btn;
    btn.style.setProperty("--p", "0%");
    $$<HTMLButtonElement>(".skill").forEach((b) => {
      b.disabled = true;
      b.classList.remove("next");
      b.setAttribute("aria-pressed", String(b === btn));
    });
    showCap(skill);
    // Space and Up belong to the game from the moment it is picked, so an early press never scrolls the page away
    if (skill === "games") window.addEventListener("keydown", blockKeys);
    M.mode = "stage";
    const fx = ({ design: 0.36, uiux: 0.3, code: 0.72, games: 0.22, ai: 0.8 } as Record<string, number>)[skill] ?? 0.5;
    const spot = stageSpot(fx);
    await jumpP(spot, { next: "stage", shape: REST, arc: 90 });
    // during a performance A's line stays up until the next one replaces it. On
    // phones the bubble would cover the screen and the chat, so A keeps quiet
    // there (the caption tells the story) and only delivers the closing line.
    const quiet = small() && (skill === "uiux" || skill === "ai");
    const line = (t: string, closing = false) => { if (!quiet || closing) say(t, { hold: 0 }); };
    try {
      if (skill === "design") {
        setCostume({ hand: "pen" });
        M.pose = "tool";
        line("Design: everything on purpose. Watch him draw the logo I live in.");
        beat(0);
        logo.classList.add("on");
        const paths = $$<SVGPathElement>("path:not(.a-slot)", logo);
        paths.forEach((p) => {
          const L = p.getTotalLength();
          p.style.transition = "none";
          p.style.strokeDasharray = String(L);
          p.style.strokeDashoffset = String(L);
        });
        await wait(60);
        paths.forEach((p, i) => {
          p.style.transition = `stroke-dashoffset ${RM ? 0.01 : 1.6}s ease ${i * 0.4}s, fill 0.8s`;
          p.style.strokeDashoffset = "0";
        });
        await wait(2900);
        beat(1);
        logo.classList.add("filled");
        await wait(1500);
        beat(2);
        setCostume({});
        const sl = () => slotFor(logo);
        await jumpP(sl, { next: "slot", slotEl: logo, shape: LOGO, scale: sl().s, arc: 140 });
        M.limbST = 0;
        line("Home sweet home.");
        await wait(2400);
        M.limbST = 1;
        await jumpP(spot, { next: "stage", shape: REST, arc: 110 });
        logo.classList.remove("on", "filled");
      } else if (skill === "uiux") {
        line("UI/UX: first the flow, then the pixels.");
        beat(0);
        ux.classList.add("on");
        await wait(2600);
        beat(1);
        ux.classList.add("real");
        M.expr = "happy";
        await wait(2600);
        beat(2);
        M.pose = "point";
        M.expr = M.sceneExpr;
        line("Now the test: can someone book in one tap?");
        await wait(2400);
        ux.classList.add("tap");
        M.sqV = -4;
        await wait(160);
        ux.classList.remove("tap");
        ux.classList.add("done");
        M.pose = "tada";
        M.expr = "happy";
        line("One tap. Booked. That's UX.");
        await wait(2800);
        ux.classList.remove("on");
        await wait(400);
        ux.classList.remove("real", "done");
        if (quiet) {
          line("One tap. Booked. That's UX.", true);
          await wait(2400);
        }
      } else if (skill === "code") {
        code.classList.add("on");
        M.pose = "rest";
        line("Code: he builds what he designs. Each line changes me, live.");
        await typeCode(code, [`const a = mascot("A");`, `a.lean = -0.25;`, `a.mood = "happy";`, `a.wave();`, `// that's the whole trick`], (i) => {
          M.sqV = -3;
          if (i === 1) { beat(0); M.shpT = { ...REST, k: -0.25 }; }
          if (i === 2) { beat(1); M.expr = "happy"; }
          if (i === 3) { beat(2); M.pose = "wave"; }
        });
        await wait(2600);
        M.shpT = { ...REST };
        code.classList.remove("on");
        await wait(300);
        code.innerHTML = "";
      } else if (skill === "motion") {
        line("Motion: see me squash before the jump? That's anticipation.");
        beat(0);
        // a long crouch is the anticipation; the beat flips as A leaves the floor
        later(RM ? 0 : 1300, () => beat(1));
        await jumpP(spot, { next: "stage", shape: REST, keepBubble: true, crouch: RM ? 0 : 1.3, arc: 210, dur: 1.15, spin: -360 });
        beat(2);
        M.pose = "tada";
        M.expr = "happy";
        line("And the wobble when I land? Follow-through.");
        await wait(2800);
      } else if (skill === "3d") {
        line("3D: Unity worlds, WebGL, and me with a bit of depth.");
        beat(0);
        M.alphaT = 0;
        await wait(300);
        a3d.classList.remove("on");
        void a3d.offsetWidth;
        a3d.classList.add("on");
        await wait(1100);
        beat(1);
        await wait(RM ? 1200 : 3700);
        beat(2);
        a3d.classList.remove("on");
        M.alphaT = 1;
        M.sqV = -6;
        await wait(1400);
      } else if (skill === "games") {
        run.classList.add("on");
        if (RM) {
          beat(2);
          line("This one is a little race, and your device asks for less motion, so I'll sit it out.");
          await wait(3200);
        } else {
          line("Games: rules, feedback, a reason to try again. Watch me first.");
          beat(0);
          await runner(run, hud, 3, true, spot, fx);
          beat(1);
          hud.textContent = "Get ready";
          line("Your turn. Click the stage or press Space to make me jump.");
          await wait(2200);
          const score = await runner(run, hud, 5, false, spot, fx);
          beat(2);
          hud.textContent = `Score · ${score} / 5`;
          M.pose = "tada";
          M.expr = score >= 3 ? "happy" : "surprised";
          line(score === 5 ? "Five out of five. Clean run." : score >= 3 ? `${score} out of 5. One more go and you'd have it.` : `${score} out of 5. Don't worry, I'm hard to steer.`);
          await wait(2600);
          if (gameReady) {
            line("Want a real challenge? There's a harder game hidden on this site. Ask me about it.");
            await wait(3400);
          }
        }
        run.classList.remove("on");
        await wait(300);
        hud.textContent = "";
      } else if (skill === "ai") {
        const q = $("p.chat-q", chat)!, a = $("p.chat-a", chat)!;
        line("AI: help that shows up in context. Watch a trainee ask.");
        chat.classList.add("on");
        beat(0);
        await typeInto(q, "Can I cross aisle 3 now?");
        await wait(1000);
        beat(1);
        M.pose = "think";
        M.expr = "think";
        M.lookOverride = { x: -1.6, y: -1.8 };
        a.innerHTML = `<span class="dots">Reading the scene</span>`;
        await wait(2200);
        beat(2);
        M.lookOverride = null;
        M.expr = "happy";
        M.pose = "point";
        await typeInto(a, "Not yet. A forklift is coming from your left. Wait for the green light, then cross.");
        await wait(2600);
        chat.classList.remove("on");
        await wait(300);
        line("That's how the AI instructor in Warehouse Safety Training works. Me? I run on if-statements.", true);
        await wait(4000);
      }
      beat(99);
      btn.classList.add("done");
      M.pose = M.scenePose;
      M.expr = M.sceneExpr;
      setCostume({});
      await jumpP(stageSpot(0.5), { next: "perch", shape: REST, arc: 70 });
    } finally {
      window.removeEventListener("keydown", blockKeys);
      setCostume({});
      M.lookOverride = null;
      if ((M.mode as Mode) !== "jump") M.mode = "perch";
      M.kind = "perch";
      M.sceneId = "play";
      M.alphaT = 1;
      performing = false;
      capBtn = null;
      ux.classList.remove("on", "real", "tap", "done");
      run.classList.remove("on");
      chat.classList.remove("on");
      $$("p", chat).forEach((p) => (p.textContent = ""));
      const all = $$<HTMLButtonElement>(".skill");
      all.forEach((b) => { b.disabled = false; b.setAttribute("aria-pressed", "false"); });
      // nudge toward the next skill not seen yet
      const at = all.indexOf(btn as HTMLButtonElement);
      [...all.slice(at + 1), ...all.slice(0, at)].find((b) => !b.classList.contains("done"))?.classList.add("next");
    }
  }

  /* ---------- page transition: A drags a cloth across, the route changes behind it ---------- */
  let transitioning = false;
  let routeWaiter: { path: string; resolve: () => void } | null = null;
  function handScreen(side: "L" | "R"): Pt {
    const { w, h, k, r, leg } = M.shp;
    const yb = -leg - r, ts = h * 0.42;
    const off = w / 2 + r * 0.55 - 1;
    const len = 14.5 * M.limbS;
    const a = rad(side === "L" ? M.armL : M.armR);
    const sx = -k * ts + (side === "L" ? -off : off);
    return { x: M.vx + M.s * (sx + len * Math.cos(a)), y: M.vy + M.s * (yb - ts + len * Math.sin(a)) };
  }
  // u runs in A's direction of travel; the cloth spans from its tail to the
  // edge A holds, pinched at the hand and trailing at the top and bottom corners
  function drawCloth(dir: number, tailU: number, leadU: number, handY: number, now: number) {
    const W = innerWidth, H = innerHeight;
    const X = (u: number) => (dir > 0 ? u : W - u).toFixed(1);
    const lagT = 64 + Math.sin(now / 240) * 10;
    const lagB = 104 + Math.sin(now / 290) * 14;
    const tt = tailU + Math.sin(now / 200) * 12;
    const tb = tailU - 34;
    const d = `M${X(tt)},0 L${X(leadU - lagT)},0 Q${X(leadU - 8)},${(handY * 0.5).toFixed(1)} ${X(leadU)},${handY.toFixed(1)} Q${X(leadU - 8)},${(handY + (H - handY) * 0.45).toFixed(1)} ${X(leadU - lagB)},${H} L${X(tb)},${H} Q${X(tailU - 48)},${(H / 2).toFixed(1)} ${X(tt)},0 Z`;
    cloth.style.clipPath = `path("${d}")`;
  }
  function tween(ms: number, fn: (t: number) => void, easing: (t: number) => number) {
    return new Promise<void>((res) => {
      const t0 = performance.now();
      const step = (now: number) => {
        if (!alive) return;
        const t = clamp((now - t0) / ms, 0, 1);
        fn(easing(t));
        if (t < 1) requestAnimationFrame(step); else res();
      };
      requestAnimationFrame(step);
    });
  }
  function ensureOut() {
    if (!introDone) introDone = true;
    setState("out");
    J = null;
    M.eyeS = M.eyeST = 1;
    M.limbS = M.limbST = 1;
    M.spin = 0; M.rotT = 0;
    sleeping = false;
    zzz.classList.remove("on");
  }
  function placeHere() {
    const el = sceneAt() || sceneEls[0];
    M.pin = null;
    M.sceneId = null; M.kind = null;
    M.shp = { ...REST }; M.shpT = { ...REST };
    M.s = M.sT = restScale();
    if (!el) { const p = floatPos(); M.vx = p.x; M.vy = p.y; M.mode = "float"; return; }
    const want = targetFor(el);
    const p = want.fn();
    M.vx = p.x; M.vy = p.y;
    M.s = M.sT = want.scale;
    M.mode = want.kind; M.kind = want.kind; M.sceneId = want.id;
  }
  function waitRoute(target: string) {
    return new Promise<void>((resolve) => {
      const done = () => { routeWaiter = null; resolve(); };
      routeWaiter = { path: target, resolve: done };
      later(8000, () => { if (routeWaiter?.resolve === done) done(); });
    });
  }
  function clothFor(url: URL): ClothLabel & { dir: number } {
    const to = normPath(url.pathname);
    if (to === "/") {
      const h = url.hash.slice(1);
      const title = h === "contact" ? "Start a project" : h === "about" ? "About" : h === "ask" ? "Ask A" : h === "work" || h.startsWith("p-") ? "All work" : "Home";
      return { kick: "Back to", title, dir: -1 };
    }
    const up = path.startsWith(to + "/");
    const l = opts.labels[to];
    return { kick: up ? "Back to" : l?.kick ?? "", title: l?.title ?? "", dir: up ? -1 : 1 };
  }
  /** After a route lands: honour #anchors ourselves, instantly, so A knows where it stands. */
  function settleHash(url: URL) {
    const id = decodeURIComponent(url.hash.slice(1));
    const el = id ? document.getElementById(id) : null;
    if (!el) return;
    root.style.scrollBehavior = "auto";
    el.scrollIntoView({ block: id.startsWith("p-") ? "center" : "start" });
    root.style.scrollBehavior = "";
  }

  async function transition(href: string, url: URL) {
    const { kick, title, dir } = clothFor(url);
    transitioning = true;
    hideBubble();
    ensureOut();
    const W = innerWidth, H = innerHeight;
    const X = (u: number) => (dir > 0 ? u : W - u);
    const floorY = () => innerHeight * 0.72;
    const startU = small() ? 44 : 76;
    const exitU = W + (small() ? 230 : 310);
    const side = dir > 0 ? "L" : "R";
    const handU = () => { const p = handScreen(side); return { u: dir > 0 ? p.x : W - p.x, y: p.y }; };
    const target = normPath(url.pathname);
    let failed = false;
    try {
      // 1. A hops to the edge of the screen to grab the cloth
      await jumpP(() => ({ x: X(startU), y: floorY() }), {
        next: "cloth", shape: { ...REST, k: -0.32 * dir }, scale: restScale() * 1.08, arc: 110, dur: 0.42,
      });
      M.expr = "effort";
      M.pose = dir > 0 ? "pull" : "pullL";
      [M.armL, M.armR] = poseAngles(M.pose, 0);
      M.walkAmp = 1;
      clothKick.textContent = kick;
      clothTitle.textContent = title;
      cloth.classList.toggle("rev", dir < 0);
      const h0 = handU();
      drawCloth(dir, -280, h0.u, h0.y, performance.now());
      cloth.hidden = false;
      // 2. drags it across: the current page disappears under it
      let last = performance.now();
      await tween(820, (t) => {
        const now = performance.now();
        M.walkPh += ((now - last) / 1000) * 17;
        last = now;
        M.vx = X(lerp(startU, exitU, t));
        M.vy = floorY() - Math.abs(Math.sin(M.walkPh)) * 3;
        const hp = handU();
        drawCloth(dir, -280, hp.u, hp.y, now);
      }, easeInOut2);
      // 3. behind the cloth, the route changes
      M.walkAmp = 0;
      const arrived = waitRoute(target);
      opts.push(href);
      await arrived;
      await frames(2);
      refreshPage();
      settleHash(url);
      await wait(140);
      // 4. the tail slides off, and A hops back in onto the new page
      const hopIn = new Promise<void>((res) => later(380, () => {
        M.pin = null; M.sceneId = null; M.kind = null;
        const el = sceneAt() || sceneEls[0];
        M.vx = X(W + 90); M.vy = floorY();
        M.expr = "happy";
        if (!el) { placeHere(); return res(); }
        const want = targetFor(el);
        jumpTo(want.fn, { scale: want.scale, next: want.kind, shape: REST, arc: 190, dur: 0.75, onLand: landHere(want, res) });
      }));
      await tween(640, (t) => drawCloth(dir, lerp(-280, W + 220, t), exitU + 500, H * 0.4, performance.now()), smooth);
      cloth.hidden = true;
      await hopIn;
    } catch (err) {
      failed = true;
      console.error(err);
      try { refreshPage(); placeHere(); } catch { /* keep the page usable */ }
    } finally {
      cloth.hidden = true;
      cloth.style.clipPath = "";
      M.walkAmp = 0;
      transitioning = false;
    }
    if (failed || !alive) return;
    M.sqV = -5;
    enterScene(M.sceneId);
  }

  /** The route changed without the cloth (back/forward, reduced motion): A hops over. */
  function arrive() {
    pend = null;
    if (M.mode === "slot" && M.slotEl && !M.slotEl.isConnected) ensureOut();
    if (M.mode === "slot" && !introDone) return;
    if (M.mode === "slot" && M.slotEl === navLogo) return; // resting in the logo: stay there
    if (performing) return;
    J = null;
    M.pin = null; M.sceneId = null; M.kind = null;
    const el = sceneAt() || sceneEls[0];
    if (!el) return placeHere();
    const want = targetFor(el);
    M.shpT = { ...REST };
    jumpTo(want.fn, { scale: want.scale, next: want.kind, shape: REST, onLand: landHere(want, () => enterScene(want.id)) });
  }

  /* ---------- the loop ---------- */
  let nextBlink = performance.now() + 2500;
  let blinkT = -1;
  function blinkNow() { blinkT = 0; }
  let last = performance.now();
  function frame(now: number) {
    if (!alive) return;
    const dt = Math.min(1 / 30, (now - last) / 1000);
    last = now;
    M.t += dt;

    // position by mode
    if (playing) stepGame(now);
    if (M.mode === "jump" && J) stepJump(dt);
    else if (M.mode === "slot" && M.slotEl) {
      const sl = slotFor(M.slotEl);
      M.vx = sl.x; M.vy = sl.y; M.s = M.sT = sl.s;
    } else if (M.mode === "perch") {
      const el = sceneEl(M.sceneId);
      const pe = el && perchEl(el);
      if (pe && pe.isConnected) { const p = perchPos(pe); M.vx = p.x; M.vy = p.y; }
    } else if (M.mode === "stage") {
      if (stagePerchEl) M.vy = stagePerchEl.getBoundingClientRect().top;
    } else if (M.mode === "float") {
      const p = floatPos();
      M.vx = p.x; M.vy = p.y + (RM ? 0 : Math.sin(M.t * 2.2) * 3);
    } else if (M.mode === "drag" && drag && drag.tx != null && drag.ty != null) {
      M.vx = lerp(M.vx, drag.tx, 0.35); M.vy = lerp(M.vy, drag.ty, 0.35);
      M.legSplay = 4 + Math.sin(M.t * 18) * 3;
    }

    if ((M.mode === "perch" || M.mode === "float") && !performing && introDone && !gameOn) watchScenes(now);    // once per visit, after a while on the site, A whispers an offer
    if (!offered && !gameOn && gameReady && introDone && !sleeping && !performing && !transitioning && bubble.hidden && (M.mode === "perch" || M.mode === "float") && now - bornAt > 40_000) {
      offered = true;
      offerGame();
    }

    // springs and easing toward targets
    const a = 1 - Math.exp(-dt * 9);
    for (const key of Object.keys(M.shp) as (keyof Shape)[]) M.shp[key] += (M.shpT[key] - M.shp[key]) * a;
    if (M.mode !== "jump" && M.mode !== "slot") M.s += (M.sT - M.s) * (1 - Math.exp(-dt * 8));
    M.eyeS += (M.eyeST - M.eyeS) * (1 - Math.exp(-dt * 10));
    M.limbS += (M.limbST - M.limbS) * (1 - Math.exp(-dt * 8));
    M.propS += (M.propST - M.propS) * (1 - Math.exp(-dt * 11));
    M.alpha += (M.alphaT - M.alpha) * (1 - Math.exp(-dt * 12));
    M.blush += (M.blushT - M.blush) * (1 - Math.exp(-dt * 6));
    M.rot += (M.rotT - M.rot) * (1 - Math.exp(-dt * 10));
    const breathe = (M.mode === "perch" || M.mode === "float" || M.mode === "stage") && !RM ? Math.sin(M.t * 2.4) * 0.018 : 0;
    const target = M.mode === "jump" ? M.sqT : 1 + breathe;
    const acc = 380 * (target - M.sq) - 16 * M.sqV;
    M.sqV += acc * dt;
    M.sq = clamp(M.sq + M.sqV * dt, 0.62, 1.35);
    if (M.mode !== "jump" && M.mode !== "drag") M.legSplay += (0 - M.legSplay) * a;

    // arms
    const [aL, aR] = poseAngles(M.mode === "drag" ? "drag" : M.pose, M.t);
    M.armL += (aL - M.armL) * (1 - Math.exp(-dt * 12));
    M.armR += (aR - M.armR) * (1 - Math.exp(-dt * 12));

    // blinking
    if (now > nextBlink && M.expr !== "sleep") { blinkNow(); nextBlink = now + 2200 + Math.random() * 3600; }
    if (blinkT >= 0) {
      blinkT += dt;
      const b = blinkT / 0.16;
      M.blink = b < 0.5 ? 1 - b * 1.8 : 0.1 + (b - 0.5) * 1.8;
      if (b >= 1) { M.blink = 1; blinkT = -1; }
    }

    // looking at the pointer
    if (M.lookOverride) M.lookT = { ...M.lookOverride };
    else if (px >= 0) {
      const dx = px - head.x, dy = py - (head.y + 20);
      const d = Math.hypot(dx, dy) || 1;
      const m = Math.min(1, d / 160);
      M.lookT = { x: (dx / d) * 1.8 * m, y: (dy / d) * 1.5 * m };
    }
    M.look.x += (M.lookT.x - M.look.x) * (1 - Math.exp(-dt * 10));
    M.look.y += (M.lookT.y - M.look.y) * (1 - Math.exp(-dt * 10));

    // petting and sleeping
    // petting only when A is not already talking (it would wipe an open menu)
    if (hoverSince && now - hoverSince > 1300 && !drag && !gameOn && bubble.hidden && (M.mode === "perch" || M.mode === "float")) {
      hoverSince = 0;
      M.expr = "happy";
      if (!seen.has("pet")) { seen.add("pet"); say("Hehe. That tickles.", { hold: 2000 }); }
      later(1800, () => (M.expr = M.sceneExpr));
    }
    const idle = now - lastInput;
    if (!sleeping && (M.mode === "perch" || M.mode === "float") && !performing && !gameOn) {
      if (idle > 26000) {
        sleeping = true;
        M.expr = "sleep";
        hideBubble();
        zzz.classList.add("on");
      } else if (idle > 15000 && M.expr !== "sleepy") {
        M.expr = "sleepy";
      }
    }
    if (sleeping) zzz.style.transform = `translate(${(head.x + 8).toFixed(0)}px, ${(head.y - 10).toFixed(0)}px)`;

    applyExpr();
    render();
    placeBubble();
    hit.style.transform = `translate(${box.l.toFixed(0)}px, ${box.t.toFixed(0)}px)`;
    hit.style.width = `${Math.max(44, box.w).toFixed(0)}px`;
    hit.style.height = `${Math.max(44, box.h).toFixed(0)}px`;
    hit.style.pointerEvents = M.alpha < 0.5 || gameOn ? "none" : "auto";
    raf = requestAnimationFrame(frame);
  }

  /* ---------- public face ---------- */
  const api: MascotEngine = {
    start() {
      const boot = () => {
        if (!alive || started) return;
        refreshPage();
        if (!bigLogo && !navLogo) return;
        started = true;
        const logo = bigLogo || navLogo!;
        const sl = slotFor(logo);
        M.vx = sl.x; M.vy = sl.y; M.s = M.sT = sl.s;
        render();
        // swap the painted A for the live one in the same frame
        layer.classList.add("on");
        hit.classList.add("on");
        root.classList.add("a-live");
        last = performance.now();
        raf = requestAnimationFrame(frame);
        void intro();
        fetch("/api/game/start")
          .then((r) => r.json())
          .then((d) => { if (alive) gameReady = !!d.enabled; })
          .catch(() => {});
      };
      (document.fonts?.ready ?? Promise.resolve()).then(boot, boot);
    },
    destroy() {
      alive = false;
      root.style.overflow = "";
      cancelAnimationFrame(raf);
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
      unlisten.forEach((u) => u());
      root.classList.remove("a-live");
      delete root.dataset.a;
      layer.classList.remove("on");
      hit.classList.remove("on");
      bubble.hidden = true;
      cloth.hidden = true;
    },
    routeChanged(pathname: string) {
      const p = normPath(pathname);
      if (routeWaiter && routeWaiter.path === p) {
        routeWaiter.resolve();
        return;
      }
      if (p === path && started) return;
      refreshPage();
      if (started && !transitioning) {
        void frames(2).then(() => {
          if (!alive) return;
          refreshPage();
          arrive();
        });
      }
    },
    navigate(href: string) {
      if (!started || RM || transitioning) return transitioning;
      const url = new URL(href, location.href);
      if (url.origin !== location.origin) return false;
      if (normPath(url.pathname) === path) return false;
      if (gameOn) gameStop();
      if (performing) {
        say("One second, I'm in the middle of a performance.", { hold: 2200 });
        return true;
      }
      void transition(url.pathname + url.search + url.hash, url);
      return true;
    },
    carryLetter() {
      if (!started || !["perch", "float"].includes(M.mode)) return;
      setCostume({ chest: "envelope" });
      M.pose = "hold";
      M.expr = "happy";
      say("Got it. I can't press send from inside this page, so copy it or open your mail app. I'll go wait in the logo.", { hold: 3600 });
      later(3800, () => { if (["perch", "float"].includes(M.mode)) goHome(); });
    },
  };
  return api;
}
